import { logEventString } from '@adv-frontend/yastore-modules/logger';
import {
    type OpenApiSchemaDashboardTodolist,
    type OpenApiSchemaDashboardTodolistItem,
    type OpenApiSchemaDashboardTodolistType,
} from '#shared/api/schema';
import { dashboardTodoListLoggingEvents } from './dashboardTodoListLoggingEvents';
import { consumeDashboardTodoListFirstShow } from './dashboardTodoListShownStorage';
import { type DashboardTodoListStepNavigationState } from './dashboardTodoListStepNavigation';

function getStepCompletionStats(items: OpenApiSchemaDashboardTodolistItem[]) {
    const itemsCompleted = items.filter((item) => Boolean(item.completed_at)).length;

    return {
        items_total: items.length,
        items_completed: itemsCompleted,
        is_all_completed: items.length > 0 && itemsCompleted === items.length,
    };
}

function getStepNavigationAdditional(stepNavigation: DashboardTodoListStepNavigationState | null) {
    if (stepNavigation == null) {
        return {
            can_move_forward: false,
            can_move_backward: false,
            move_backward_available_for_seconds: 0,
        };
    }

    return {
        can_move_forward: stepNavigation.canMoveForward,
        can_move_backward: stepNavigation.canMoveBackward,
        move_backward_available_for_seconds: stepNavigation.moveBackwardAvailableForSeconds,
    };
}

function getStepAdditional(todolist: OpenApiSchemaDashboardTodolist) {
    return {
        step_type: todolist.type,
        step_title: todolist.title,
        step_display_sequence: todolist.display_sequence,
        ...getStepCompletionStats(todolist.items),
    };
}

export function logDashboardTodoListShow(params: {
    todolist: OpenApiSchemaDashboardTodolist;
    stepNavigation: DashboardTodoListStepNavigationState | null;
}) {
    const { todolist, stepNavigation } = params;

    logEventString(dashboardTodoListLoggingEvents.Show, 'true', {
        is_first_show: consumeDashboardTodoListFirstShow(),
        ...getStepAdditional(todolist),
        ...getStepNavigationAdditional(stepNavigation),
    });
}

export function logDashboardTodoListItemToggleComplete(params: {
    item: OpenApiSchemaDashboardTodolistItem;
    stepType: OpenApiSchemaDashboardTodolistType;
}) {
    const { item, stepType } = params;
    const willBeComplete = !item.completed_at;

    logEventString(dashboardTodoListLoggingEvents.ItemToggleComplete, willBeComplete ? 'complete' : 'incomplete', {
        item_id: item.id,
        item_title: item.title,
        step_type: stepType,
    });
}

export function logDashboardTodoListStepMoveForward(params: {
    todolist: OpenApiSchemaDashboardTodolist;
    stepNavigation: DashboardTodoListStepNavigationState | null;
}) {
    const { todolist, stepNavigation } = params;

    logEventString(dashboardTodoListLoggingEvents.StepMoveForward, 'true', {
        ...getStepAdditional(todolist),
        ...getStepNavigationAdditional(stepNavigation),
    });
}

export function logDashboardTodoListStepMoveBackward(params: {
    todolist: OpenApiSchemaDashboardTodolist;
    stepNavigation: DashboardTodoListStepNavigationState | null;
}) {
    const { todolist, stepNavigation } = params;

    logEventString(dashboardTodoListLoggingEvents.StepMoveBackward, 'true', {
        ...getStepAdditional(todolist),
        ...getStepNavigationAdditional(stepNavigation),
    });
}

export function logDashboardTodoListMarkdownLinkClick(params: {
    href: string;
    itemId: string;
    stepType: OpenApiSchemaDashboardTodolistType;
}) {
    const { href, itemId, stepType } = params;
    const isExternalLink = href.startsWith('http') || href.startsWith('//');

    logEventString(dashboardTodoListLoggingEvents.MarkdownLinkClick, href, {
        item_id: itemId,
        step_type: stepType,
        is_external: isExternalLink,
        link_href: href,
    });
}
